import { defineStore } from 'pinia';
import type { Address } from 'viem';
import { Chain, mainnet, sepolia, arbitrum, optimism, polygon, base } from 'viem/chains';

export type UniswapConfig = {
  quoter?: Address;
  router?: Address;
  poolFee?: number; // 500, 3000, 10000
};

export type ChainConfig = {
  id: number;
  label: string;
  chain: Chain;
  rpcUrl?: string;
  explorerBase?: string;
  uniswap?: UniswapConfig;
};

function readEnv(name: string): string | undefined {
  const v = (process.env as any)[name];
  return v && `${v}`.trim() !== '' ? (v as string) : undefined;
}

function buildChains(): ChainConfig[] {
  const sepoliaCfg: ChainConfig = {
    id: sepolia.id,
    label: 'Sepolia',
    chain: sepolia,
    rpcUrl: readEnv('VUE_APP_RPC_URL_SEPOLIA') || 'https://rpc.sepolia.org',
    explorerBase: readEnv('VUE_APP_BLOCK_EXPLORER_SEPOLIA') || 'https://sepolia.etherscan.io',
    uniswap: {
      quoter: readEnv('VUE_APP_UNISWAP_QUOTER_ADDRESS') as Address | undefined,
      router: readEnv('VUE_APP_UNISWAP_ROUTER_ADDRESS') as Address | undefined,
      poolFee: Number(readEnv('VUE_APP_UNISWAP_POOL_FEE') || '3000')
    }
  };
  const mainnetCfg: ChainConfig = {
    id: mainnet.id,
    label: 'Ethereum',
    chain: mainnet,
    rpcUrl: readEnv('VUE_APP_RPC_URL_MAINNET') || 'https://ethereum.publicnode.com',
    explorerBase: readEnv('VUE_APP_BLOCK_EXPLORER_MAINNET') || 'https://etherscan.io',
    uniswap: {
      // Verified code present at these addresses across mainnet, arbitrum, optimism, polygon
      quoter: (readEnv('VUE_APP_UNISWAP_QUOTER_ADDRESS_MAINNET') as Address | undefined) || '0x61fFE014bA17989E743c5F6cB21bF9697530B21e',
      router: (readEnv('VUE_APP_UNISWAP_ROUTER_ADDRESS_MAINNET') as Address | undefined) || '0xE592427A0AEce92De3Edee1F18E0157C05861564',
      poolFee: Number(readEnv('VUE_APP_UNISWAP_POOL_FEE_MAINNET') || '3000')
    }
  };
  const arbitrumCfg: ChainConfig = {
    id: arbitrum.id,
    label: 'Arbitrum',
    chain: arbitrum,
    rpcUrl: readEnv('VUE_APP_RPC_URL_ARBITRUM') || 'https://arb1.arbitrum.io/rpc',
    explorerBase: readEnv('VUE_APP_BLOCK_EXPLORER_ARBITRUM') || 'https://arbiscan.io',
    uniswap: {
      quoter: (readEnv('VUE_APP_UNISWAP_QUOTER_ADDRESS_ARBITRUM') as Address | undefined) || '0x61fFE014bA17989E743c5F6cB21bF9697530B21e',
      router: (readEnv('VUE_APP_UNISWAP_ROUTER_ADDRESS_ARBITRUM') as Address | undefined) || '0xE592427A0AEce92De3Edee1F18E0157C05861564',
      poolFee: Number(readEnv('VUE_APP_UNISWAP_POOL_FEE_ARBITRUM') || '3000')
    }
  };
  const optimismCfg: ChainConfig = {
    id: optimism.id,
    label: 'Optimism',
    chain: optimism,
    rpcUrl: readEnv('VUE_APP_RPC_URL_OPTIMISM') || 'https://mainnet.optimism.io',
    explorerBase: readEnv('VUE_APP_BLOCK_EXPLORER_OPTIMISM') || 'https://optimistic.etherscan.io',
    uniswap: {
      quoter: (readEnv('VUE_APP_UNISWAP_QUOTER_ADDRESS_OPTIMISM') as Address | undefined) || '0x61fFE014bA17989E743c5F6cB21bF9697530B21e',
      router: (readEnv('VUE_APP_UNISWAP_ROUTER_ADDRESS_OPTIMISM') as Address | undefined) || '0xE592427A0AEce92De3Edee1F18E0157C05861564',
      poolFee: Number(readEnv('VUE_APP_UNISWAP_POOL_FEE_OPTIMISM') || '3000')
    }
  };
  const polygonCfg: ChainConfig = {
    id: polygon.id,
    label: 'Polygon',
    chain: polygon,
    rpcUrl: readEnv('VUE_APP_RPC_URL_POLYGON') || 'https://polygon-rpc.com',
    explorerBase: readEnv('VUE_APP_BLOCK_EXPLORER_POLYGON') || 'https://polygonscan.com',
    uniswap: {
      quoter: (readEnv('VUE_APP_UNISWAP_QUOTER_ADDRESS_POLYGON') as Address | undefined) || '0x61fFE014bA17989E743c5F6cB21bF9697530B21e',
      router: (readEnv('VUE_APP_UNISWAP_ROUTER_ADDRESS_POLYGON') as Address | undefined) || '0xE592427A0AEce92De3Edee1F18E0157C05861564',
      poolFee: Number(readEnv('VUE_APP_UNISWAP_POOL_FEE_POLYGON') || '3000')
    }
  };
  const baseCfg: ChainConfig = {
    id: base.id,
    label: 'Base',
    chain: base,
    rpcUrl: readEnv('VUE_APP_RPC_URL_BASE') || 'https://mainnet.base.org',
    explorerBase: readEnv('VUE_APP_BLOCK_EXPLORER_BASE') || 'https://basescan.org',
    uniswap: {
      // Uniswap v3 periphery addresses differ on Base; leave blank unless configured
      quoter: readEnv('VUE_APP_UNISWAP_QUOTER_ADDRESS_BASE') as Address | undefined,
      router: readEnv('VUE_APP_UNISWAP_ROUTER_ADDRESS_BASE') as Address | undefined,
      poolFee: Number(readEnv('VUE_APP_UNISWAP_POOL_FEE_BASE') || '3000')
    }
  };
  return [sepoliaCfg, mainnetCfg, arbitrumCfg, optimismCfg, polygonCfg, baseCfg];
}

export const useChainStore = defineStore('chain', {
  state: () => ({
    supported: buildChains() as ChainConfig[],
    selectedId: Number((process.env.VUE_APP_DEFAULT_CHAIN_ID as string) || '11155111'),
  }),
  getters: {
    current(state): ChainConfig {
      const cfg = state.supported.find(c => c.id === state.selectedId) || state.supported[0];
      return cfg;
    }
  },
  actions: {
    init() {
      const saved = localStorage.getItem('selectedChainId');
      if (saved) this.selectedId = Number(saved);
    },
    select(id: number) {
      this.selectedId = id;
      localStorage.setItem('selectedChainId', String(id));
    }
  }
});
